import React, { Fragment } from "react";
import { InfoCircleTwoTone } from "@ant-design/icons";
import { Table } from "antd";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { renderKycSearchCell } from "./KycSearchHighlight";

const getSortOrder = (tableSorter, columnKey) => {
	if (tableSorter.field !== columnKey) {
		return null;
	}
	const o = String(tableSorter.order || "").toUpperCase();
	if (o === "ASC") {
		return "ascend";
	}
	if (o === "DESC") {
		return "descend";
	}
	return null;
};

const SERVER_SORTER = { compare: (_a, _b) => 0 };

const getCellValue = (record, keys) => {
	for (const key of keys) {
		const value = record?.[key];
		if (value !== undefined && value !== null && value !== "") {
			return value;
		}
	}

	return "-";
};

const formatDesignMasterDate = (value) => {
	if (!value || value === "-") {
		return "-";
	}

	const parsedDate = moment(
		value,
		["M/D/YYYY h.mm.ss A", "M/D/YYYY h:mm:ss A", moment.ISO_8601, "YYYY-MM-DD HH:mm:ss"],
		true
	);
	return parsedDate.isValid() ? parsedDate.format("DD/MM/YYYY") : "-";
};

const formatRecordCount = (total, range) => {
	if (!total) {
		return "Showing 0 records";
	}

	return `Showing ${range[0]}-${range[1]} of ${total.toLocaleString()} records`;
};

const KycDesignTab = ({
	tabTableData,
	isLoading,
	tableSorter,
	OnhandleTableSortColumn,
	navigationContext = {},
	materialDetailsContext = null,
	onDesignDetailsClick = null,
	emptyText = "No data found",
	tableSearchTerm = "",
}) => {
	const navigate = useNavigate();
	const renderRecordCell = (record, keys) =>
		renderKycSearchCell(getCellValue(record, keys), tableSearchTerm);
	const totalRecords = Number(tabTableData.totalRecords || 0);
	const currentPage = Number(tabTableData.currentPage || 1);
	const pageSize = Number(tabTableData.pageSize || 10);

	const handleTableAscDes = (pagination, filters, sorter) => {
		const s = Array.isArray(sorter) ? sorter[0] || {} : sorter || {};
		const sortOrder = s.order;
		const rawField = s.field;
		const sortField =
			rawField !== undefined && rawField !== null && rawField !== ""
				? Array.isArray(rawField)
					? rawField[0]
					: rawField
				: tableSorter.field;

		let nextOrder = tableSorter.order;
		if (sortOrder === "ascend") {
			nextOrder = "ASC";
		} else if (sortOrder === "descend") {
			nextOrder = "DESC";
		} else {
			const pageChanged =
				pagination.current !== tabTableData.currentPage ||
				(pagination.pageSize != null &&
					tabTableData.pageSize != null &&
					pagination.pageSize !== tabTableData.pageSize);
			if (!pageChanged && String(sortField) === String(tableSorter.field)) {
				const o = String(tableSorter.order || "").toUpperCase();
				nextOrder = o === "DESC" ? "ASC" : "DESC";
			}
		}

		OnhandleTableSortColumn({
			sortColumn: sortField,
			orderBy: nextOrder,
			currentPage: pagination.current,
			pageSize: pagination.pageSize,
		});
	};

	const handleDesignDetailsClick = (event, record) => {
		event.stopPropagation();
		const generalDetailId = record?.generalDetailId;
		if (!generalDetailId) {
			return;
		}

		if (onDesignDetailsClick) {
			onDesignDetailsClick(record);
			return;
		}

		if (materialDetailsContext?.productName) {
			navigate(`/kyc/design-details/${encodeURIComponent(generalDetailId)}`, {
				state: {
					generalDetailId,
					designMasterNumber: record.designMasterNumber || "",
					designMasterDate: record.designMasterDate || "",
					contact: record.contact || "",
					customerName: record.customerName || "",
					material: record.material || materialDetailsContext.productName || "",
					returnSource: "material",
					materialProductName: materialDetailsContext.productName,
					materialName: materialDetailsContext.materialName || materialDetailsContext.productName,
					groupCompanyId: materialDetailsContext.groupCompanyId || "",
					groupCompanyName: materialDetailsContext.groupCompanyName || "",
					materialsCustomer: materialDetailsContext.materialsCustomer || "",
					accountName: materialDetailsContext.accountName || "",
					returnTab: materialDetailsContext.returnTab || "materials",
				},
			});
			return;
		}

		navigate(`/kyc/design-details/${encodeURIComponent(generalDetailId)}`, {
			state: {
				generalDetailId,
				designMasterNumber: record.designMasterNumber || "",
				designMasterDate: record.designMasterDate || "",
				contact: record.contact || "",
				customerName: record.customerName || "",
				material: record.material || "",
				groupCompanyId: navigationContext.groupCompanyId || "",
				groupCompanyName: navigationContext.groupCompanyName || "",
				returnTab: "design",
			},
		});
	};

	const tableColumns = [
		{
			title: "Design Master No",
			dataIndex: "designMasterNumber",
			key: "designMasterNumber",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "designMasterNumber"),
			render: (_, record) => renderRecordCell(record, ["designMasterNumber"]),
		},
		{
			title: "Design Master Date",
			dataIndex: "designMasterDate",
			key: "designMasterDate",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 160,
			sortOrder: getSortOrder(tableSorter, "designMasterDate"),
			render: (_, record) => {
				const raw = getCellValue(record, ["designMasterDate"]);
				return renderKycSearchCell(formatDesignMasterDate(raw), tableSearchTerm);
			},
		},
		{
			title: "Design Assigned To",
			dataIndex: "designAssignTo",
			key: "designAssignTo",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "designAssignTo"),
			render: (_, record) => renderRecordCell(record, ["designAssignTo"]),
		},
		{
			title: "Contact",
			dataIndex: "contact",
			key: "contact",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "contact"),
			render: (_, record) => renderRecordCell(record, ["contact"]),
		},
		{
			title: "Design Status",
			dataIndex: "statecode",
			key: "statecode",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 160,
			sortOrder: getSortOrder(tableSorter, "statecode"),
			render: (_, record) => renderRecordCell(record, ["statecode"]),
		},
		{
			title: "Owner",
			dataIndex: "owner",
			key: "owner",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 160,
			sortOrder: getSortOrder(tableSorter, "owner"),
			render: (_, record) => renderRecordCell(record, ["owner"]),
		},
		{
			title: "Action",
			key: "action",
			width: 90,
			fixed: "right",
			render: (_, record) => (
				<div
					className={`tbl_info_details ${record?.generalDetailId ? "" : "disabled"}`}
					onClick={(event) => handleDesignDetailsClick(event, record)}
					role="presentation"
				>
					<InfoCircleTwoTone />
				</div>
			),
		},
	];

	return (
		<Fragment>
			<Table
				bordered
				scroll={{ x: 1200 }}
				className="logistic_tables kyc_logistic_tables kyc_design_tables"
				dataSource={tabTableData.rows}
				columns={tableColumns}
				sortDirections={["ascend", "descend"]}
				onChange={handleTableAscDes}
				rowKey={(record, index) => record.generalDetailId || record.designMasterNumber || index}
				locale={{ emptyText }}
				pagination={{
					total: totalRecords,
					current: currentPage,
					pageSize,
					responsive: true,
					showSizeChanger: false,
					showTotal: formatRecordCount,
				}}
				loading={isLoading}
			/>
		</Fragment>
	);
};

export default KycDesignTab;
